import { motion } from 'framer-motion';
import {
  BarChart3,
  Building2,
  CalendarDays,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Menu,
  Search,
  Settings,
  Sparkles,
  UserCircle,
  X
} from '../components/icons';
import { useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Modal from '../components/Modal';
import ThemeToggle from '../components/ThemeToggle';
import { useAuth } from '../store/authContext';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/kanban', label: 'Kanban CRM', icon: BarChart3 },
  { to: '/agenda', label: 'Agenda', icon: CalendarDays },
  { to: '/busca-ia', label: 'Busca IA', icon: Sparkles },
  { to: '/extracao/google', label: 'Extração Google', icon: Search },
  // { to: '/extracao/apolo', label: 'Extração Apollo', icon: Sparkles },
  { to: '/agente', label: 'Agente', icon: Settings },
  { to: '/configurar-whatts', label: 'Configurar Whatts', icon: Settings },
  { to: '/perfil', label: 'Perfil', icon: UserCircle },
  { to: '/configuracoes', label: 'Configurações', icon: Settings }
];

export default function AppShell() {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);

  const initials = useMemo(() => {
    const name = session?.user.name || 'BIM Prospect';
    return name
      .split(' ')
      .slice(0, 2)
      .map((v) => v[0]?.toUpperCase())
      .join('');
  }, [session?.user.name]);

  const signOut = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const canUseGoogleExtraction = useMemo(() => {
    const value = session?.user?.usa_extracao_google;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['false', '0', 'nao', 'não', 'no', 'off'].includes(normalized)) return false;
    }
    return true;
  }, [session?.user?.usa_extracao_google]);

  const filteredNavItems = useMemo(
    () => navItems.filter((item) => (item.to === '/extracao/google' ? canUseGoogleExtraction : true)),
    [canUseGoogleExtraction]
  );

  const nav = (
    <div className="space-y-1.5">
      {filteredNavItems.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm font-medium transition ${
                isActive
                  ? 'border-primary/40 bg-primary/10 text-slate-900 dark:text-white'
                  : 'border-transparent text-slate-600 hover:border-borderLight hover:bg-slate-100 dark:text-slate-300 dark:hover:border-borderDark dark:hover:bg-slate-800'
              }`
            }
            onClick={() => setMobileOpen(false)}
          >
            <span className="ui-icon-chip-soft">
              <Icon />
            </span>
            {item.label}
          </NavLink>
        );
      })}
    </div>
  );

  return (
    <div className={`min-h-screen bg-bgLight text-textLight dark:bg-bgDark dark:text-textDark lg:grid ${sidebarHidden ? 'lg:grid-cols-1' : 'lg:grid-cols-[260px_1fr]'}`}>
      {!sidebarHidden ? (
        <aside className="hidden border-r border-borderLight bg-cardLight p-4 dark:border-borderDark dark:bg-navDark lg:block">
          <Link to="/dashboard" className="mb-8 block rounded-xl border border-borderLight bg-white px-3 py-3 dark:border-borderDark dark:bg-cardDark">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">BIM PROSPECT</p>
            <p className="text-xl font-bold">CRM Premium</p>
          </Link>
          {nav}
        </aside>
      ) : null}

      <div className="relative">
        <header className="sticky top-0 z-40 border-b border-borderLight bg-bgLight/95 px-4 py-3 backdrop-blur dark:border-borderDark dark:bg-bgDark/90 lg:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-borderLight bg-cardLight dark:border-borderDark dark:bg-cardDark lg:hidden"
            >
              <span className="ui-icon-inline">
                <Menu className="h-5 w-5" />
              </span>
            </button>

            <div className="flex-1" />

            <button
              type="button"
              onClick={() => setSidebarHidden((value) => !value)}
              className="hidden h-10 items-center gap-2 rounded-xl border border-borderLight bg-cardLight px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-borderDark dark:bg-cardDark dark:text-slate-200 dark:hover:bg-slate-800 lg:inline-flex"
              title={sidebarHidden ? 'Mostrar barra lateral' : 'Esconder barra lateral'}
            >
              <span className="ui-icon-chip">{sidebarHidden ? <Menu /> : <X />}</span>
              {sidebarHidden ? 'Mostrar menu' : 'Ocultar menu'}
            </button>

            <div className="hidden sm:block">
              <ThemeToggle />
            </div>

            <button
              type="button"
              onClick={() => setProfileOpen((v) => !v)}
              className="inline-flex items-center gap-2 rounded-xl border border-borderLight bg-cardLight px-2 py-1.5 dark:border-borderDark dark:bg-cardDark"
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-xs font-bold text-white">{initials}</span>
              <span className="ui-icon-inline text-slate-500">
                <ChevronDown />
              </span>
            </button>
          </div>

          {profileOpen ? (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute right-4 top-[3.8rem] z-50 w-56 rounded-xl border border-borderLight bg-cardLight p-2 shadow-xl dark:border-borderDark dark:bg-cardDark lg:right-6"
            >
              <button
                onClick={() => {
                  setProfileOpen(false);
                  navigate('/perfil');
                }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <span className="ui-icon-chip-soft">
                  <UserCircle />
                </span>
                Perfil
              </button>
              <button
                onClick={() => {
                  setProfileOpen(false);
                  navigate('/configuracoes');
                }}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <span className="ui-icon-chip-soft">
                  <Settings />
                </span>
                Configurações
              </button>
              <button onClick={signOut} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-500 hover:bg-red-500/10">
                <span className="ui-icon-chip bg-red-50 text-red-500 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                  <LogOut />
                </span>
                Sair
              </button>
            </motion.div>
          ) : null}
        </header>

        <main className="mx-auto max-w-7xl p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      <Modal open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm font-semibold">Navegação</p>
          <button className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-borderLight dark:border-borderDark" onClick={() => setMobileOpen(false)}>
            <span className="ui-icon-inline">
              <X />
            </span>
          </button>
        </div>
        <div className="mb-4">
          <ThemeToggle />
        </div>
        {nav}
      </Modal>
    </div>
  );
}
